import {
  AnalysisPayload,
  CenterLinePoint,
  ColorCurveKind,
  ColorCurvePoint,
  ColorCurvePointPayload,
  ColorGlassProperty,
  ColorGlassPropertyModel,
  ColorIlluminant,
  ColorIlluminantPayload,
  ColorMeasurement,
  ColorMeasurementDetail,
  ColorMeasurementMethod,
  ColorMeasurementMethodPayload,
  ColorMeasurementPayload,
  ColorObserver,
  ColorObserverPayload,
  ColorReflectionCorrectionModel,
  ColorReflectionCorrectionModelPayload,
  ColorTrialColorMapping,
  ColorTrialColorMappingPayload,
  ColorTransmittanceMode,
  ColorTransmittanceModePayload,
  SavedAnalysis,
  ThermoDetail,
  ThermoImage,
  ActiveTrial,
  ThermoPixel,
  ThermoStats,
  ThermoSamplePayload,
  ThermoDataRow,
  VftSettings,
  ThicknessBottle,
  ThicknessAnalysisContract,
  ThicknessBottlePayload,
  ThicknessDataPoint,
  ThicknessMeanProfilePoint,
  ThicknessMultipleAnalysisPayload,
  ThicknessRecalcStatus,
  SavedThicknessMultipleAnalysis,
  ProductionStepMappingRow,
  VftCoefficients
} from "../types";
import { getPostgrestUrl } from "../settings";
import { enrichCenterLine } from "../utils/thermoEmissivity";

const CACHE_TTL_MS = 5 * 60 * 1000;
const POSTGREST_RETRY_ATTEMPTS = 5;
const POSTGREST_RETRY_DELAYS_MS = [200, 500, 900, 1400];
const COLOR_AUDIT_USER = "interface";
type ChemSettingRow = {
  id: number;
  timestamp: string;
  filepath?: string | null;
  Fe2O3?: number | null;
};

type CacheRecord<T> = { at: number; data: T };
const cache = new Map<string, CacheRecord<unknown>>();

function cacheKey(url: string) {
  return url;
}

async function cachedRequest<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const existing = cache.get(key) as CacheRecord<T> | undefined;
  const now = Date.now();
  if (existing && now - existing.at < CACHE_TTL_MS) {
    return existing.data;
  }
  const data = await fetcher();
  cache.set(key, { at: now, data });
  return data;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryPostgrest(status: number | null, body: string | null, errorMessage: string | null) {
  const text = `${body || ""} ${errorMessage || ""}`.toLowerCase();
  if (status === 503) return true;
  if (text.includes("pgrst002")) return true;
  if (text.includes("schema cache")) return true;
  return false;
}

async function fetchJson<T>(url: string): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < POSTGREST_RETRY_ATTEMPTS; attempt += 1) {
    let response: Response | null = null;
    try {
      response = await fetch(url, {
        headers: {
          Accept: "application/json"
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network request failed";
      lastError = new Error(`PostgREST fetch failed for ${url}: ${message}`);
      if (
        attempt < POSTGREST_RETRY_ATTEMPTS - 1 &&
        shouldRetryPostgrest(null, null, message)
      ) {
        await sleep(POSTGREST_RETRY_DELAYS_MS[Math.min(attempt, POSTGREST_RETRY_DELAYS_MS.length - 1)]);
        continue;
      }
      throw lastError;
    }

    if (!response.ok) {
      const message = await response.text();
      lastError = new Error(`PostgREST error ${response.status}: ${message}`);
      if (
        attempt < POSTGREST_RETRY_ATTEMPTS - 1 &&
        shouldRetryPostgrest(response.status, message, null)
      ) {
        await sleep(POSTGREST_RETRY_DELAYS_MS[Math.min(attempt, POSTGREST_RETRY_DELAYS_MS.length - 1)]);
        continue;
      }
      throw lastError;
    }

    return (await response.json()) as T;
  }

  throw lastError || new Error("PostgREST request failed without additional details.");
}

type TrialRow = {
  thermoimaging_id: number;
  trial_type: string;
  descriptive_name: string;
};
type VftRow = {
  thermoimaging_id: number;
  A_Fluegel: number;
  B_Fluegel: number;
  T0_Fluegel: number;
};
type VftSettingsRow = {
  timestamp: string;
  A_Fluegel: number;
  B_Fluegel: number;
  T0_Fluegel: number;
  A_AI: number;
  B_AI: number;
  T0_AI: number;
};
type ThermoFileIndexRow = {
  id: number;
  folder_name: string;
  filename: string;
  timestamp: string;
};
type ThicknessIndexRow = {
  id: number;
  trial_name: string;
  relative_bottle_id: number;
  timestamp: string | null;
};
type ThermoStatPoint = {
  temperature: number;
  viscosity: number;
};
type TimestampRow = {
  timestamp: string | null;
};
type TimestampWindow = {
  trial_start_timestamp: string | null;
  trial_end_timestamp: string | null;
};

type TrialDurationRow = {
  id: number;
  descriptive_name: string;
  trial_type: string;
  start_time: string;
  end_time: string | null;
};

const ENABLE_TRIALS_FETCH = import.meta.env.VITE_ENABLE_TRIALS_FETCH !== "false";
const ENABLE_THICKNESS_TRIALS_FETCH = import.meta.env.VITE_ENABLE_THICKNESS_TRIALS_FETCH !== "false";

function ensureBaseUrl() {
  const baseUrl = getPostgrestUrl();
  if (!baseUrl) {
    throw new Error("PostgREST URL is not configured. Update it in Settings.");
  }
  return baseUrl;
}

function addAuditDefaults<T extends Record<string, unknown>>(payload: T, isCreate = false): T {
  const next = { ...payload } as T;
  if (isCreate && !next.created_by) {
    (next as Record<string, unknown>).created_by = COLOR_AUDIT_USER;
  }
  if (!next.updated_by) {
    (next as Record<string, unknown>).updated_by = COLOR_AUDIT_USER;
  }
  return next;
}

function buildUuidFilter(ids: string[]): string {
  if (!ids.length) return "";
  return ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
}

async function mutateRows<T>(
  url: string,
  method: "POST" | "PATCH",
  payload: unknown,
  mode: "representation" | "minimal" = "representation"
): Promise<T[]> {
  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Prefer: mode === "minimal" ? "return=minimal" : "return=representation"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const rawBody = await response.text();
    let message = rawBody;
    try {
      const parsed = JSON.parse(rawBody) as {
        code?: string;
        message?: string;
        details?: string;
        hint?: string;
      };
      const parts = [parsed.code, parsed.message, parsed.details, parsed.hint]
        .filter((part) => typeof part === "string" && part.trim())
        .map((part) => String(part).trim());
      if (parts.length) {
        message = parts.join(" | ");
      }
    } catch {
      // keep raw text response
    }
    throw new Error(`PostgREST ${method} failed (${response.status}): ${message}`);
  }
  if (mode === "minimal") {
    return [];
  }
  return (await response.json()) as T[];
}

async function createRow<T>(table: string, payload: Record<string, unknown>, select = "*"): Promise<T> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/${table}?select=${encodeURIComponent(select)}`;
  const rows = await mutateRows<T>(url, "POST", addAuditDefaults(payload, true), "representation");
  if (!rows.length) {
    throw new Error(`Create failed for ${table}: no rows returned.`);
  }
  return rows[0];
}

async function updateRow<T>(
  table: string,
  id: string,
  payload: Record<string, unknown>,
  select = "*"
): Promise<T> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/${table}?id=eq.${id}&select=${encodeURIComponent(select)}`;
  const rows = await mutateRows<T>(url, "PATCH", addAuditDefaults(payload, false), "representation");
  if (!rows.length) {
    throw new Error(`Update failed for ${table}: no rows returned.`);
  }
  return rows[0];
}

async function deleteRow(table: string, id: string) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/${table}?id=eq.${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Delete failed for ${table}: ${message}`);
  }
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function clampNullable(value: unknown, min: number, max: number): number | null {
  const numeric = toFiniteNumber(value);
  if (numeric == null) return null;
  if (numeric < min) return min;
  if (numeric > max) return max;
  return numeric;
}

function positiveNullable(value: unknown): number | null {
  const numeric = toFiniteNumber(value);
  return numeric != null && numeric > 0 ? numeric : null;
}

function nonNegativeNullable(value: unknown): number | null {
  const numeric = toFiniteNumber(value);
  return numeric != null && numeric >= 0 ? numeric : null;
}

function sanitizeColorMeasurementWritePayload<T extends Record<string, unknown>>(payload: T): T {
  const next = { ...payload } as Record<string, unknown>;

  const clampPercentFields = [
    "applied_surface_reflectivity_pct",
    "input_fe2o3_percent",
    "brightness_percent",
    "tau_v_percent",
    "fe2_percent",
    "fe_total_percent"
  ];
  clampPercentFields.forEach((field) => {
    if (!(field in next)) return;
    next[field] = clampNullable(next[field], 0, 100);
  });

  const positiveFields = [
    "thickness_top_mm",
    "thickness_middle_mm",
    "thickness_bottom_mm",
    "thickness_mean_mm",
    "dominant_wavelength_nm"
  ];
  positiveFields.forEach((field) => {
    if (!(field in next)) return;
    next[field] = positiveNullable(next[field]);
  });

  if ("fe2_fe3_ratio" in next) {
    next.fe2_fe3_ratio = nonNegativeNullable(next.fe2_fe3_ratio);
  }
  if ("fe2_fe_total_percent" in next) {
    next.fe2_fe_total_percent = nonNegativeNullable(next.fe2_fe_total_percent);
  }
  if ("fit_rmse" in next) {
    next.fit_rmse = nonNegativeNullable(next.fit_rmse);
  }
  if ("fit_point_count" in next) {
    const numeric = nonNegativeNullable(next.fit_point_count);
    next.fit_point_count = numeric == null ? null : Math.round(numeric);
  }
  if ("measurement_label" in next && typeof next.measurement_label === "string") {
    const trimmed = next.measurement_label.trim();
    next.measurement_label = trimmed || null;
  }
  if ("glass_color" in next && typeof next.glass_color === "string") {
    const trimmed = next.glass_color.trim();
    next.glass_color = trimmed || null;
  }
  if ("wavelength_mode" in next) {
    const allowed = new Set(["dominant", "complementary", "none"]);
    const value = typeof next.wavelength_mode === "string" ? next.wavelength_mode.trim().toLowerCase() : "";
    next.wavelength_mode = allowed.has(value) ? value : null;
  }

  return next as T;
}

async function fetchColorAdminTable<T>(table: string, select: string, order?: string): Promise<T[]> {
  const baseUrl = ensureBaseUrl();
  const params = [`select=${encodeURIComponent(select)}`];
  if (order) {
    params.push(`order=${encodeURIComponent(order)}`);
  }
  const url = `${baseUrl}/${table}?${params.join("&")}`;
  const key = cacheKey(url);
  return cachedRequest<T[]>(key, () => fetchJson<T[]>(url));
}

export async function fetchThermoMetadata(): Promise<ThermoImage[]> {
  const baseUrl = ensureBaseUrl();
  const baseSelect = "id,timestamp,cal_name,epsilon,prod_step,folder_name,filename,height";
  const thermoUrl = `${baseUrl}/thermoimaging?select=${encodeURIComponent(
    baseSelect
  )}&order=timestamp.desc`;
  const thermoKey = cacheKey(thermoUrl);

  const images = await cachedRequest<ThermoImage[]>(thermoKey, () =>
    fetchJson<ThermoImage[]>(thermoUrl)
  );

  let trials: TrialRow[] = [];
  if (ENABLE_TRIALS_FETCH) {
    const trialsUrl = `${baseUrl}/thermoimaging_active_trials?select=thermoimaging_id,trial_type,descriptive_name`;
    const trialsKey = cacheKey(trialsUrl);
    try {
      trials = await cachedRequest<TrialRow[]>(trialsKey, () => fetchJson<TrialRow[]>(trialsUrl));
    } catch (err) {
      console.warn("Trial fetch failed, continuing without trial filters", err);
      trials = [];
    }
  }

  let vftRows: VftRow[] = [];
  try {
    // View expected: thermoimaging_vft_active with latest VFT per thermoimaging timestamp
    const vftUrl = `${baseUrl}/thermoimaging_vft_active?select=thermoimaging_id,A_Fluegel,B_Fluegel,T0_Fluegel`;
    const vftKey = cacheKey(vftUrl);
    vftRows = await cachedRequest<VftRow[]>(vftKey, () => fetchJson<VftRow[]>(vftUrl));
  } catch (err) {
    console.warn("VFT fetch failed, continuing without VFT coefficients", err);
    vftRows = [];
  }

  const trialMap = new Map<number, ActiveTrial[]>();
  trials.forEach((trial) => {
    const list = trialMap.get(trial.thermoimaging_id) || [];
    list.push({ trial_type: trial.trial_type, descriptive_name: trial.descriptive_name });
    trialMap.set(trial.thermoimaging_id, list);
  });

  const vftMap = new Map<number, VftCoefficients>();
  vftRows.forEach((row) => {
    vftMap.set(row.thermoimaging_id, {
      A_Fluegel: row.A_Fluegel,
      B_Fluegel: row.B_Fluegel,
      T0_Fluegel: row.T0_Fluegel
    });
  });

  return images.map((img) => ({
    ...img,
    active_trials: trialMap.get(img.id) || [],
    vftCoeffs: vftMap.get(img.id)
  }));
}

export async function fetchThermoFileIndex(): Promise<ThermoFileIndexRow[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thermoimaging?select=${encodeURIComponent(
    "id,folder_name,filename,timestamp"
  )}`;
  const key = cacheKey(url);
  return cachedRequest<ThermoFileIndexRow[]>(key, () => fetchJson<ThermoFileIndexRow[]>(url));
}

export async function fetchVftSettingsForTimestamp(
  timestamp: string
): Promise<VftSettings | null> {
  if (!timestamp) {
    throw new Error("Timestamp is required to resolve VFT settings.");
  }
  const baseUrl = ensureBaseUrl();
  const selectFields = "timestamp,A_Fluegel,B_Fluegel,T0_Fluegel,A_AI,B_AI,T0_AI";
  const filter = encodeURIComponent(`lte.${timestamp}`);
  const url = `${baseUrl}/vft_settings?select=${encodeURIComponent(
    selectFields
  )}&timestamp=${filter}&order=timestamp.desc&limit=1`;
  const key = cacheKey(url);
  const rows = await cachedRequest<VftSettingsRow[]>(key, () => fetchJson<VftSettingsRow[]>(url));
  if (!rows.length) return null;
  return rows[0];
}

export async function fetchProductionStepMapping(): Promise<ProductionStepMappingRow[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/production_step_mapping?select=${encodeURIComponent(
    "canonical_name,allowed,aliases"
  )}`;
  const key = cacheKey(url);
  return cachedRequest<ProductionStepMappingRow[]>(key, () =>
    fetchJson<ProductionStepMappingRow[]>(url)
  );
}

export async function fetchHeatmapData(sampleId: number): Promise<ThermoPixel[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thermoimaging_data?sample_id=eq.${sampleId}&select=x,y,temperature,viscosity`;
  const key = cacheKey(url);
  return cachedRequest<ThermoPixel[]>(key, () => fetchJson<ThermoPixel[]>(url));
}

export async function detectThermoAnomalies(
  ids: number[]
): Promise<Record<number, boolean>> {
  if (!ids.length) return {};

  const baseUrl = ensureBaseUrl();
  const response = await fetch(`${baseUrl}/rpc/detect_thermo_anomalies`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ ids })
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Anomaly detection failed: ${message}`);
  }

  const payload = (await response.json()) as unknown;
  const data =
    Array.isArray(payload) && payload.length === 1 && typeof payload[0] === "object"
      ? payload[0]
      : payload;

  if (!data || typeof data !== "object") {
    return {};
  }

  const entries = Object.entries(data as Record<string, unknown>)
    .map(([key, value]) => [Number(key), Boolean(value)] as const)
    .filter(([key]) => !Number.isNaN(key));

  return Object.fromEntries(entries);
}

type ThicknessTrialRow = {
  thickness_id: number;
  trial_type: string;
  descriptive_name: string;
};

export async function fetchThicknessMetadata(): Promise<ThicknessBottle[]> {
  const baseUrl = ensureBaseUrl();
  const baseSelect =
    "id,trial_name,relative_bottle_id,timestamp,bottle_profile,number_of_slices,inner_volume,outer_volume,glass_volume,glass_mass,global_min_x,global_min_y,global_min_z,global_max_x,global_max_y,global_max_z";
  const thicknessUrl = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    baseSelect
  )}&order=timestamp.desc`;
  const thicknessKey = cacheKey(thicknessUrl);

  const bottles = await cachedRequest<ThicknessBottle[]>(thicknessKey, () =>
    fetchJson<ThicknessBottle[]>(thicknessUrl)
  );

  let trials: ThicknessTrialRow[] = [];
  if (ENABLE_THICKNESS_TRIALS_FETCH) {
    const trialsUrl = `${baseUrl}/thickness_active_trials?select=thickness_id,trial_type,descriptive_name`;
    const trialsKey = cacheKey(trialsUrl);
    try {
      trials = await cachedRequest<ThicknessTrialRow[]>(trialsKey, () =>
        fetchJson<ThicknessTrialRow[]>(trialsUrl)
      );
    } catch (err) {
      console.warn("Thickness trial fetch failed, continuing without trials", err);
      trials = [];
    }
  }

  const trialMap = new Map<number, ActiveTrial[]>();
  trials.forEach((trial) => {
    const list = trialMap.get(trial.thickness_id) || [];
    list.push({ trial_type: trial.trial_type, descriptive_name: trial.descriptive_name });
    trialMap.set(trial.thickness_id, list);
  });

  return bottles.map((bottle) => ({
    ...bottle,
    active_trials: trialMap.get(bottle.id) || []
  }));
}

export async function fetchThicknessData(
  ids: number[]
): Promise<Record<number, ThicknessDataPoint[]>> {
  if (!ids.length) return {};
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const url = `${baseUrl}/thickness_bottles?select=id,data&id=${encodeURIComponent(filter)}`;
  const rows = await fetchJson<{ id: number; data: ThicknessDataPoint[] | null }[]>(url);
  const map: Record<number, ThicknessDataPoint[]> = {};
  rows.forEach((row) => {
    map[row.id] = Array.isArray(row.data) ? row.data : [];
  });
  return map;
}

export async function fetchThicknessBottleSchemaVersions(
  ids: number[]
): Promise<Record<number, string | null>> {
  if (!ids.length) return {};
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const selectFields =
    "id,analysis_schema_version:analysis_payload->>schema_version,analysis_analyzer_schema:analysis_payload->analyzer_meta->>schema_version";
  const url = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    selectFields
  )}&id=${encodeURIComponent(filter)}`;

  try {
    const rows = await fetchJson<
      { id: number; analysis_schema_version?: string | null; analysis_analyzer_schema?: string | null }[]
    >(url);
    const schemaById: Record<number, string | null> = {};
    rows.forEach((row) => {
      const schemaFromTopLevel =
        typeof row.analysis_schema_version === "string" && row.analysis_schema_version.trim()
          ? row.analysis_schema_version.trim()
          : null;
      const schemaFromAnalyzer =
        typeof row.analysis_analyzer_schema === "string" && row.analysis_analyzer_schema.trim()
          ? row.analysis_analyzer_schema.trim()
          : null;
      const schema = schemaFromTopLevel ?? schemaFromAnalyzer;
      schemaById[row.id] = schema;
    });
    return schemaById;
  } catch (err) {
    const fallbackRows = await fetchThicknessBottleHeavy(ids, { includeAnalysisPayload: true });
    const schemaById: Record<number, string | null> = {};
    fallbackRows.forEach((row) => {
      const payload = row.analysis_payload;
      const schema =
        payload && typeof payload === "object"
          ? typeof payload.schema_version === "string"
            ? payload.schema_version
            : typeof payload.analyzer_meta?.schema_version === "string"
              ? payload.analyzer_meta.schema_version
              : null
          : null;
      schemaById[row.id] = schema;
    });
    console.warn("Schema-version lookup fell back to heavy fetch", err);
    return schemaById;
  }
}

export type ThicknessBottleAnalysisState = {
  schemaVersion: string | null;
  recalcStatus: ThicknessRecalcStatus | null;
};

function parseThicknessRecalcStatus(value: unknown): ThicknessRecalcStatus | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const status = value as Record<string, unknown>;
  if (status.state !== "failed") return null;
  const failedAt = typeof status.failed_at === "string" ? status.failed_at : "";
  const targetSchemaVersion =
    typeof status.target_schema_version === "string" ? status.target_schema_version : "";
  const errorMessage = typeof status.error_message === "string" ? status.error_message : "";
  const trialName = typeof status.trial_name === "string" ? status.trial_name : "";
  const relativeBottleId = Number(status.relative_bottle_id);
  if (!failedAt || !targetSchemaVersion || !errorMessage || !trialName || !Number.isFinite(relativeBottleId)) {
    return null;
  }
  return {
    state: "failed",
    failed_at: failedAt,
    target_schema_version: targetSchemaVersion,
    error_message: errorMessage,
    trial_name: trialName,
    relative_bottle_id: relativeBottleId,
    source_fingerprint:
      typeof status.source_fingerprint === "string" ? status.source_fingerprint : null
  };
}

export async function fetchThicknessBottleAnalysisStates(
  ids: number[]
): Promise<Record<number, ThicknessBottleAnalysisState>> {
  if (!ids.length) return {};
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const recalcStateUrl = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    "id,recalc_state:analysis_payload->recalc_status->>state"
  )}&id=${encodeURIComponent(filter)}`;
  const [schemaById, recalcStateRows] = await Promise.all([
    fetchThicknessBottleSchemaVersions(ids),
    fetchJson<Array<{ id: number; recalc_state?: string | null }>>(recalcStateUrl)
  ]);
  const failedIds = recalcStateRows
    .filter((row) => row.recalc_state === "failed")
    .map((row) => row.id);
  let failedDetailsById: Record<number, ThicknessRecalcStatus> = {};
  if (failedIds.length) {
    const failedFilter = failedIds.length === 1 ? `eq.${failedIds[0]}` : `in.(${failedIds.join(",")})`;
    const detailSelectFields = [
      "id",
      "recalc_state:analysis_payload->recalc_status->>state",
      "recalc_failed_at:analysis_payload->recalc_status->>failed_at",
      "recalc_target_schema_version:analysis_payload->recalc_status->>target_schema_version",
      "recalc_error_message:analysis_payload->recalc_status->>error_message",
      "recalc_trial_name:analysis_payload->recalc_status->>trial_name",
      "recalc_relative_bottle_id:analysis_payload->recalc_status->>relative_bottle_id",
      "recalc_source_fingerprint:analysis_payload->recalc_status->>source_fingerprint"
    ].join(",");
    const detailUrl = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
      detailSelectFields
    )}&id=${encodeURIComponent(failedFilter)}`;
    const detailRows = await fetchJson<
      Array<{
        id: number;
        recalc_state?: string | null;
        recalc_failed_at?: string | null;
        recalc_target_schema_version?: string | null;
        recalc_error_message?: string | null;
        recalc_trial_name?: string | null;
        recalc_relative_bottle_id?: string | number | null;
        recalc_source_fingerprint?: string | null;
      }>
    >(detailUrl);
    failedDetailsById = Object.fromEntries(
      detailRows
        .map((row) => [
          row.id,
          parseThicknessRecalcStatus({
            state: row.recalc_state,
            failed_at: row.recalc_failed_at,
            target_schema_version: row.recalc_target_schema_version,
            error_message: row.recalc_error_message,
            trial_name: row.recalc_trial_name,
            relative_bottle_id: row.recalc_relative_bottle_id,
            source_fingerprint: row.recalc_source_fingerprint
          })
        ] as const)
        .filter((entry): entry is readonly [number, ThicknessRecalcStatus] => !!entry[1])
    );
  }
  const stateById: Record<number, ThicknessBottleAnalysisState> = {};
  ids.forEach((id) => {
    stateById[id] = {
      schemaVersion: schemaById[id] ?? null,
      recalcStatus: failedDetailsById[id] ?? null
    };
  });
  return stateById;
}

type ThicknessBottleHeavyRow = {
  id: number;
  mean_thickness_profile?: ThicknessMeanProfilePoint[] | null;
  analysis_payload?: ThicknessAnalysisContract | null;
};

type ThicknessMultipleAnalysisHeavyRow = {
  id: number;
  mean_thickness_profile?: ThicknessMeanProfilePoint[] | null;
  analysis_payload?: ThicknessAnalysisContract | null;
};

export async function fetchThicknessBottleHeavy(
  ids: number[],
  options?: { includeAnalysisPayload?: boolean }
): Promise<ThicknessBottleHeavyRow[]> {
  if (!ids.length) return [];
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const includeAnalysisPayload = options?.includeAnalysisPayload ?? true;
  const selectFields = includeAnalysisPayload
    ? "id,mean_thickness_profile,analysis_payload"
    : "id,mean_thickness_profile";
  const url = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    selectFields
  )}&id=${encodeURIComponent(filter)}`;
  const key = cacheKey(url);
  try {
    return await cachedRequest<ThicknessBottleHeavyRow[]>(key, () =>
      fetchJson<ThicknessBottleHeavyRow[]>(url)
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : "unknown error";
    throw new Error(`Thickness heavy fetch failed: ${message}`);
  }
}

export async function fetchThicknessMultipleAnalysisHeavy(
  id: number
): Promise<ThicknessMultipleAnalysisHeavyRow | null> {
  const baseUrl = ensureBaseUrl();
  const selectFields = "id,mean_thickness_profile,analysis_payload";
  const url = `${baseUrl}/thickness_multiple_analyses?select=${encodeURIComponent(
    selectFields
  )}&id=eq.${id}&limit=1`;
  const key = cacheKey(url);
  try {
    const rows = await cachedRequest<ThicknessMultipleAnalysisHeavyRow[]>(key, () =>
      fetchJson<ThicknessMultipleAnalysisHeavyRow[]>(url)
    );
    return rows[0] || null;
  } catch (err) {
    const message = err instanceof Error ? err.message : "unknown error";
    throw new Error(`Thickness heavy fetch failed: ${message}`);
  }
}

function computeTimestampWindow(rows: TimestampRow[]): TimestampWindow {
  let minMs = Number.POSITIVE_INFINITY;
  let maxMs = Number.NEGATIVE_INFINITY;
  let minIso: string | null = null;
  let maxIso: string | null = null;

  rows.forEach((row) => {
    if (!row.timestamp) return;
    const parsed = Date.parse(row.timestamp);
    if (Number.isNaN(parsed)) return;
    if (parsed < minMs) {
      minMs = parsed;
      minIso = row.timestamp;
    }
    if (parsed > maxMs) {
      maxMs = parsed;
      maxIso = row.timestamp;
    }
  });

  return {
    trial_start_timestamp: minIso,
    trial_end_timestamp: maxIso
  };
}

async function fetchTimestampWindowForThickness(ids: number[]): Promise<TimestampWindow> {
  if (!ids.length) {
    return { trial_start_timestamp: null, trial_end_timestamp: null };
  }
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const url = `${baseUrl}/thickness_bottles?select=timestamp&id=${encodeURIComponent(filter)}`;
  const rows = await fetchJson<TimestampRow[]>(url);
  return computeTimestampWindow(rows);
}

async function fetchTimestampWindowForThermo(ids: number[]): Promise<TimestampWindow> {
  if (!ids.length) {
    return { trial_start_timestamp: null, trial_end_timestamp: null };
  }
  const baseUrl = ensureBaseUrl();
  const filter = ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(",")})`;
  const url = `${baseUrl}/thermoimaging?select=timestamp&id=${encodeURIComponent(filter)}`;
  const rows = await fetchJson<TimestampRow[]>(url);
  return computeTimestampWindow(rows);
}

export async function saveThicknessMultipleAnalysis(
  payload: ThicknessMultipleAnalysisPayload
): Promise<SavedThicknessMultipleAnalysis> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_multiple_analyses`;
  const timestampWindow = await fetchTimestampWindowForThickness(payload.bottle_id_array);
  const payloadWithWindow: ThicknessMultipleAnalysisPayload = {
    ...payload,
    ...timestampWindow
  };
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(payloadWithWindow)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness multiple save failed: ${message}`);
  }
  const rows = (await response.json()) as SavedThicknessMultipleAnalysis[];
  if (!rows.length) {
    throw new Error("Thickness multiple save failed: no rows returned.");
  }
  return rows[0];
}

export async function loadThicknessMultipleAnalyses(): Promise<SavedThicknessMultipleAnalysis[]> {
  const baseUrl = ensureBaseUrl();
  const selectFields =
    "id,name,bottle_id_array,timestamp,number_of_bottles,bottle_profile,number_of_slices";
  const url = `${baseUrl}/thickness_multiple_analyses?select=${encodeURIComponent(
    selectFields
  )}&order=timestamp.desc`;
  const key = cacheKey(url);
  return cachedRequest<SavedThicknessMultipleAnalysis[]>(key, () =>
    fetchJson<SavedThicknessMultipleAnalysis[]>(url)
  );
}

export async function updateThicknessMultipleAnalysis(
  id: number,
  payload: ThicknessMultipleAnalysisPayload
): Promise<SavedThicknessMultipleAnalysis> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_multiple_analyses?id=eq.${id}`;
  const timestampWindow = await fetchTimestampWindowForThickness(payload.bottle_id_array);
  const payloadWithWindow: ThicknessMultipleAnalysisPayload = {
    ...payload,
    ...timestampWindow
  };
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(payloadWithWindow)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness multiple update failed: ${message}`);
  }
  const rows = (await response.json()) as SavedThicknessMultipleAnalysis[];
  if (!rows.length) {
    throw new Error("Thickness multiple update failed: no rows returned.");
  }
  return rows[0];
}

export async function deleteThicknessMultipleAnalysis(id: number) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_multiple_analyses?id=eq.${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness multiple delete failed: ${message}`);
  }
}

export async function fetchThicknessIndex(): Promise<ThicknessIndexRow[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    "id,trial_name,relative_bottle_id,timestamp"
  )}`;
  return fetchJson<ThicknessIndexRow[]>(url);
}

export async function createThicknessBottle(
  payload: ThicknessBottlePayload
): Promise<{ id: number }> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_bottles?select=id`;
  const { id, ...rest } = payload;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(rest)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness insert failed: ${message}`);
  }
  const rows = (await response.json()) as Array<{ id: number }>;
  if (!rows.length) {
    throw new Error("Thickness insert failed: no rows returned.");
  }
  return rows[0];
}

export async function updateThicknessBottle(
  id: number,
  payload: ThicknessBottlePayload
): Promise<{ id: number }> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thickness_bottles?id=eq.${id}&select=id`;
  const { id: _ignored, ...rest } = payload;
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(rest)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness update failed: ${message}`);
  }
  const rows = (await response.json()) as Array<{ id: number }>;
  if (!rows.length) {
    throw new Error("Thickness update failed: no rows returned.");
  }
  return rows[0];
}

export async function markThicknessBottleRecalcFailed(
  id: number,
  recalcStatus: ThicknessRecalcStatus
): Promise<{ id: number }> {
  const baseUrl = ensureBaseUrl();
  const fetchUrl = `${baseUrl}/thickness_bottles?select=${encodeURIComponent(
    "id,analysis_payload"
  )}&id=eq.${id}&limit=1`;
  const existingRows = await fetchJson<Array<{ id: number; analysis_payload?: ThicknessAnalysisContract | null }>>(
    fetchUrl
  );
  if (!existingRows.length) {
    throw new Error(`Thickness recalc failure mark failed: bottle ${id} not found.`);
  }
  const existingPayload =
    existingRows[0].analysis_payload &&
    typeof existingRows[0].analysis_payload === "object" &&
    !Array.isArray(existingRows[0].analysis_payload)
      ? existingRows[0].analysis_payload
      : null;
  const basePayload =
    existingPayload && typeof existingPayload.schema_version === "string" && existingPayload.schema_version.trim()
      ? existingPayload
      : {
          ...(existingPayload || {}),
          schema_version: "thickness_analysis.failed"
        };
  const nextPayload: ThicknessAnalysisContract = {
    ...basePayload,
    recalc_status: recalcStatus
  };
  const updateUrl = `${baseUrl}/thickness_bottles?id=eq.${id}&select=id`;
  const response = await fetch(updateUrl, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify({ analysis_payload: nextPayload })
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thickness recalc failure mark failed: ${message}`);
  }
  const rows = (await response.json()) as Array<{ id: number }>;
  if (!rows.length) {
    throw new Error("Thickness recalc failure mark failed: no rows returned.");
  }
  cache.clear();
  return rows[0];
}

export async function createThermoImage(payload: ThermoSamplePayload): Promise<ThermoImage> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thermoimaging`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=representation"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thermoimaging insert failed: ${message}`);
  }
  const rows = (await response.json()) as ThermoImage[];
  if (!rows.length) {
    throw new Error("Thermoimaging insert failed: no rows returned.");
  }
  return rows[0];
}

export async function insertThermoData(sampleId: number, rows: ThermoDataRow[]) {
  if (!rows.length) return;
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thermoimaging_data`;
  const chunkSize = 2000;
  for (let i = 0; i < rows.length; i += chunkSize) {
    const chunk = rows.slice(i, i + chunkSize).map((row) => ({
      sample_id: sampleId,
      x: Number(row.x),
      y: Number(row.y),
      temperature: Number(row.temperature),
      viscosity: Number(row.viscosity),
      bottle: Boolean(row.bottle),
      center_line: Boolean(row.center_line)
    }));
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Prefer: "return=minimal"
      },
      body: JSON.stringify(chunk)
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Thermoimaging data insert failed: ${message}`);
    }
  }
}

export async function fetchCenterLine(
  sampleId: number,
  epsilon?: number | null
): Promise<ThermoDetail> {
  const baseUrl = ensureBaseUrl();
  const centerLineUrl = `${baseUrl}/thermoimaging_data?sample_id=eq.${sampleId}&center_line=eq.true&select=x,y,temperature,viscosity&order=y.asc`;
  const centerLineKey = cacheKey(centerLineUrl);
  const centerLinePromise = cachedRequest<CenterLinePoint[]>(centerLineKey, () =>
    fetchJson<CenterLinePoint[]>(centerLineUrl)
  );

  const bottleUrl = `${baseUrl}/thermoimaging_data?sample_id=eq.${sampleId}&bottle=eq.true&select=temperature,viscosity`;
  const bottleKey = cacheKey(bottleUrl);
  const bottlePromise = cachedRequest<ThermoStatPoint[]>(bottleKey, () =>
    fetchJson<ThermoStatPoint[]>(bottleUrl)
  );

  const [centerLineResult, bottleResult] = await Promise.allSettled([
    centerLinePromise,
    bottlePromise
  ]);

  if (centerLineResult.status === "rejected") {
    throw centerLineResult.reason;
  }

  const centerLine = enrichCenterLine(centerLineResult.value, epsilon);
  const stats = computeStats(centerLine);

  let bottlePoints: ThermoStatPoint[] = [];
  if (bottleResult.status === "fulfilled") {
    bottlePoints = bottleResult.value;
  } else {
    console.warn("Bottle stats fetch failed, continuing without it.", bottleResult.reason);
  }

  const bottleStats = {
    ...computeStats(bottlePoints),
    count: bottlePoints.length
  };

  return { id: sampleId, epsilon: Number(epsilon ?? 0.875), centerLine, stats, bottleStats };
}

export async function saveAnalysis(payload: AnalysisPayload) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/multiple_analyses`;
  const timestampWindow = await fetchTimestampWindowForThermo(payload.sample_id_array);
  const payloadWithWindow: AnalysisPayload = {
    ...payload,
    ...timestampWindow
  };
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payloadWithWindow)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Save failed: ${message}`);
  }
}

export async function loadAnalyses(): Promise<SavedAnalysis[]> {
  const baseUrl = ensureBaseUrl();
  const selectFields =
    "id,name,sample_id_array,mean_center_lines,min_temperature,mean_temperature,max_temperature,min_viscosity,mean_viscosity,max_viscosity,min_bottle_temperature,mean_bottle_temperature,max_bottle_temperature,min_bottle_viscosity,mean_bottle_viscosity,max_bottle_viscosity,height_reference_sample_id,height_reference_line,height_reference_height_mm,timestamp,trial_start_timestamp,trial_end_timestamp";
  const url = `${baseUrl}/multiple_analyses?select=${encodeURIComponent(selectFields)}&order=timestamp.desc`;
  const key = cacheKey(url);
  return cachedRequest<SavedAnalysis[]>(key, () => fetchJson<SavedAnalysis[]>(url));
}

export async function updateAnalysis(id: number, payload: Partial<AnalysisPayload>) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/multiple_analyses?id=eq.${id}`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Prefer: "return=minimal"
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Update failed: ${message}`);
  }
}

export function clearCache() {
  cache.clear();
}

export async function deleteAnalysis(id: number) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/multiple_analyses?id=eq.${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Delete failed: ${message}`);
  }
}

export async function deleteThermoImage(id: number) {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/thermoimaging?id=eq.${id}`;
  const response = await fetch(url, { method: "DELETE" });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Thermoimaging delete failed: ${message}`);
  }
}

const COLOR_MEASUREMENT_SELECT = [
  "id",
  "measurement_method_id",
  "glass_property_id",
  "reference_measurement_id",
  "measurement_label",
  "measured_at",
  "measured_by",
  "instrument_name",
  "instrument_serial",
  "source_csv_path",
  "source_file_checksum",
  "calculation_version",
  "applied_wavelength_min_nm",
  "applied_wavelength_max_nm",
  "applied_wavelength_step_nm",
  "applied_transmittance_mode_id",
  "applied_reflection_correction_model_id",
  "applied_surface_reflectivity_pct",
  "applied_standard_thickness_mm",
  "applied_observer_id",
  "applied_illuminant_id",
  "applied_reference_medium_name",
  "applied_reference_medium_notes",
  "thickness_top_mm",
  "thickness_middle_mm",
  "thickness_bottom_mm",
  "thickness_mean_mm",
  "input_fe2o3_percent",
  "glass_color",
  "glass_color_trial_duration_id",
  "l_star",
  "a_star",
  "b_star",
  "x_chromaticity",
  "y_chromaticity",
  "x_tristimulus",
  "y_tristimulus",
  "z_tristimulus",
  "brightness_percent",
  "tau_v_percent",
  "dominant_wavelength_nm",
  "wavelength_mode",
  "excitation_purity",
  "cieluv_saturation",
  "fe2_percent",
  "fe_total_percent",
  "fe2_fe_total_percent",
  "fe2_fe3_ratio",
  "fit_slope",
  "fit_intercept",
  "fit_r2",
  "fit_rmse",
  "fit_point_count",
  "calculation_notes",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by"
].join(",");

export async function fetchTrialDurations(): Promise<TrialDurationRow[]> {
  const baseUrl = ensureBaseUrl();
  const selectFields = "id,descriptive_name,trial_type,start_time,end_time";
  const url = `${baseUrl}/trial_duration?select=${encodeURIComponent(selectFields)}&order=${encodeURIComponent("start_time.desc")}`;
  const key = cacheKey(url);
  return cachedRequest<TrialDurationRow[]>(key, () => fetchJson<TrialDurationRow[]>(url));
}

export async function fetchColorTrialColorMappings(): Promise<ColorTrialColorMapping[]> {
  const selectFields =
    "id,trial_duration_id,glass_color,notes,created_at,created_by,updated_at,updated_by";
  return fetchColorAdminTable<ColorTrialColorMapping>(
    "color_trial_color_mappings",
    selectFields,
    "updated_at.desc"
  );
}

export async function createColorTrialColorMapping(
  payload: ColorTrialColorMappingPayload
): Promise<ColorTrialColorMapping> {
  return createRow<ColorTrialColorMapping>(
    "color_trial_color_mappings",
    payload,
    "id,trial_duration_id,glass_color,notes,created_at,created_by,updated_at,updated_by"
  );
}

export async function updateColorTrialColorMapping(
  id: string,
  payload: Partial<ColorTrialColorMappingPayload>
): Promise<ColorTrialColorMapping> {
  return updateRow<ColorTrialColorMapping>(
    "color_trial_color_mappings",
    id,
    payload,
    "id,trial_duration_id,glass_color,notes,created_at,created_by,updated_at,updated_by"
  );
}

export async function deleteColorTrialColorMapping(id: string) {
  await deleteRow("color_trial_color_mappings", id);
}

export async function fetchChemSettingsFe2O3(): Promise<ChemSettingRow[]> {
  const baseUrl = ensureBaseUrl();
  const selectFields = "id,timestamp,filepath,Fe2O3";
  const url = `${baseUrl}/chem_settings?select=${encodeURIComponent(selectFields)}&order=${encodeURIComponent("timestamp.asc")}`;
  const key = cacheKey(url);
  return cachedRequest<ChemSettingRow[]>(key, () => fetchJson<ChemSettingRow[]>(url));
}

export async function fetchColorObservers(): Promise<ColorObserver[]> {
  const selectFields =
    "id,code,name,observer_angle_deg,standard_reference,notes,created_at,created_by,updated_at,updated_by";
  return fetchColorAdminTable<ColorObserver>("color_observers", selectFields, "code.asc");
}

export async function createColorObserver(payload: ColorObserverPayload): Promise<ColorObserver> {
  return createRow<ColorObserver>("color_observers", payload, "id,code,name,observer_angle_deg,standard_reference,notes,created_at,created_by,updated_at,updated_by");
}

export async function updateColorObserver(id: string, payload: ColorObserverPayload): Promise<ColorObserver> {
  return updateRow<ColorObserver>("color_observers", id, payload, "id,code,name,observer_angle_deg,standard_reference,notes,created_at,created_by,updated_at,updated_by");
}

export async function deleteColorObserver(id: string) {
  await deleteRow("color_observers", id);
}

export async function fetchColorIlluminants(): Promise<ColorIlluminant[]> {
  const selectFields =
    "id,code,name,standard_reference,is_historical,notes,created_at,created_by,updated_at,updated_by";
  return fetchColorAdminTable<ColorIlluminant>("color_illuminants", selectFields, "code.asc");
}

export async function createColorIlluminant(
  payload: ColorIlluminantPayload
): Promise<ColorIlluminant> {
  return createRow<ColorIlluminant>("color_illuminants", payload, "id,code,name,standard_reference,is_historical,notes,created_at,created_by,updated_at,updated_by");
}

export async function updateColorIlluminant(
  id: string,
  payload: ColorIlluminantPayload
): Promise<ColorIlluminant> {
  return updateRow<ColorIlluminant>("color_illuminants", id, payload, "id,code,name,standard_reference,is_historical,notes,created_at,created_by,updated_at,updated_by");
}

export async function deleteColorIlluminant(id: string) {
  await deleteRow("color_illuminants", id);
}

export async function fetchColorTransmittanceModes(): Promise<ColorTransmittanceMode[]> {
  const selectFields =
    "id,code,name,description,notes,created_at,created_by,updated_at,updated_by";
  return fetchColorAdminTable<ColorTransmittanceMode>("color_transmittance_modes", selectFields, "code.asc");
}

export async function createColorTransmittanceMode(
  payload: ColorTransmittanceModePayload
): Promise<ColorTransmittanceMode> {
  return createRow<ColorTransmittanceMode>("color_transmittance_modes", payload, "id,code,name,description,notes,created_at,created_by,updated_at,updated_by");
}

export async function updateColorTransmittanceMode(
  id: string,
  payload: ColorTransmittanceModePayload
): Promise<ColorTransmittanceMode> {
  return updateRow<ColorTransmittanceMode>("color_transmittance_modes", id, payload, "id,code,name,description,notes,created_at,created_by,updated_at,updated_by");
}

export async function deleteColorTransmittanceMode(id: string) {
  await deleteRow("color_transmittance_modes", id);
}

export async function fetchColorReflectionCorrectionModels(): Promise<ColorReflectionCorrectionModel[]> {
  const selectFields =
    "id,code,name,description,requires_reflectivity_pct,notes,created_at,created_by,updated_at,updated_by";
  return fetchColorAdminTable<ColorReflectionCorrectionModel>(
    "color_reflection_correction_models",
    selectFields,
    "code.asc"
  );
}

export async function createColorReflectionCorrectionModel(
  payload: ColorReflectionCorrectionModelPayload
): Promise<ColorReflectionCorrectionModel> {
  return createRow<ColorReflectionCorrectionModel>("color_reflection_correction_models", payload, "id,code,name,description,requires_reflectivity_pct,notes,created_at,created_by,updated_at,updated_by");
}

export async function updateColorReflectionCorrectionModel(
  id: string,
  payload: ColorReflectionCorrectionModelPayload
): Promise<ColorReflectionCorrectionModel> {
  return updateRow<ColorReflectionCorrectionModel>("color_reflection_correction_models", id, payload, "id,code,name,description,requires_reflectivity_pct,notes,created_at,created_by,updated_at,updated_by");
}

export async function deleteColorReflectionCorrectionModel(id: string) {
  await deleteRow("color_reflection_correction_models", id);
}

export async function fetchColorGlassProperties(): Promise<ColorGlassProperty[]> {
  const selectFields =
    "id,code,name,description,notes,is_active,created_at,updated_at";
  return fetchColorAdminTable<ColorGlassProperty>("color_glass_properties", selectFields, "name.asc");
}

export async function fetchColorGlassPropertyModels(): Promise<ColorGlassPropertyModel[]> {
  const selectFields = [
    "id",
    "glass_property_id",
    "model_kind",
    "coefficient_jsonb",
    "source_medium",
    "wavelength_min_nm",
    "wavelength_max_nm",
    "source_reference",
    "is_default",
    "created_at",
    "created_by",
    "updated_at",
    "updated_by"
  ].join(",");
  return fetchColorAdminTable<ColorGlassPropertyModel>(
    "color_glass_property_models",
    selectFields,
    "glass_property_id.asc,is_default.desc"
  );
}

export async function fetchColorMeasurementMethods(): Promise<ColorMeasurementMethod[]> {
  const selectFields = [
    "id",
    "name",
    "description",
    "wavelength_min_nm",
    "wavelength_max_nm",
    "wavelength_step_nm",
    "transmittance_mode_id",
    "reflection_correction_model_id",
    "surface_reflectivity_pct_default",
    "standard_thickness_mm",
    "observer_id",
    "illuminant_id",
    "reference_medium_name",
    "reference_medium_notes",
    "is_active",
    "created_at",
    "created_by",
    "updated_at",
    "updated_by"
  ].join(",");
  return fetchColorAdminTable<ColorMeasurementMethod>("color_measurement_methods", selectFields, "name.asc");
}

export async function createColorMeasurementMethod(
  payload: ColorMeasurementMethodPayload
): Promise<ColorMeasurementMethod> {
  return createRow<ColorMeasurementMethod>("color_measurement_methods", payload, "id,name,description,wavelength_min_nm,wavelength_max_nm,wavelength_step_nm,transmittance_mode_id,reflection_correction_model_id,surface_reflectivity_pct_default,standard_thickness_mm,observer_id,illuminant_id,reference_medium_name,reference_medium_notes,is_active,created_at,created_by,updated_at,updated_by");
}

export async function updateColorMeasurementMethod(
  id: string,
  payload: ColorMeasurementMethodPayload
): Promise<ColorMeasurementMethod> {
  return updateRow<ColorMeasurementMethod>("color_measurement_methods", id, payload, "id,name,description,wavelength_min_nm,wavelength_max_nm,wavelength_step_nm,transmittance_mode_id,reflection_correction_model_id,surface_reflectivity_pct_default,standard_thickness_mm,observer_id,illuminant_id,reference_medium_name,reference_medium_notes,is_active,created_at,created_by,updated_at,updated_by");
}

export async function deleteColorMeasurementMethod(id: string) {
  await deleteRow("color_measurement_methods", id);
}

export async function fetchColorMeasurements(): Promise<ColorMeasurement[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/color_measurements?select=${encodeURIComponent(COLOR_MEASUREMENT_SELECT)}&order=${encodeURIComponent("measured_at.desc")}`;
  const key = cacheKey(url);
  return cachedRequest<ColorMeasurement[]>(key, () => fetchJson<ColorMeasurement[]>(url));
}

export async function fetchColorMeasurementsByIds(ids: string[]): Promise<ColorMeasurement[]> {
  if (!ids.length) return [];
  const baseUrl = ensureBaseUrl();
  const filter = buildUuidFilter(ids);
  const url = `${baseUrl}/color_measurements?select=${encodeURIComponent(COLOR_MEASUREMENT_SELECT)}&id=${encodeURIComponent(filter)}&order=${encodeURIComponent("measured_at.desc")}`;
  const key = cacheKey(url);
  return cachedRequest<ColorMeasurement[]>(key, () => fetchJson<ColorMeasurement[]>(url));
}

export async function fetchColorMeasurementIndex(): Promise<
  Array<{
    id: string;
    measurement_label?: string | null;
    source_csv_path?: string | null;
    source_file_checksum?: string | null;
    glass_color?: string | null;
    glass_color_trial_duration_id?: number | null;
    measured_at: string;
  }>
> {
  const baseUrl = ensureBaseUrl();
  const selectFields =
    "id,measurement_label,source_csv_path,source_file_checksum,glass_color,glass_color_trial_duration_id,measured_at";
  const url = `${baseUrl}/color_measurements?select=${encodeURIComponent(selectFields)}`;
  return fetchJson<
    Array<{
      id: string;
      measurement_label?: string | null;
      source_csv_path?: string | null;
      source_file_checksum?: string | null;
      glass_color?: string | null;
      glass_color_trial_duration_id?: number | null;
      measured_at: string;
    }>
  >(url);
}

export async function fetchColorMeasurementCurves(
  ids: string[]
): Promise<Record<string, Partial<Record<ColorCurveKind, ColorCurvePoint[]>>>> {
  if (!ids.length) return {};
  const baseUrl = ensureBaseUrl();
  const filter = buildUuidFilter(ids);
  const url = `${baseUrl}/color_measurement_curve_points?select=${encodeURIComponent(
    "measurement_id,curve_kind,wavelength_nm,value,created_at"
  )}&measurement_id=${encodeURIComponent(filter)}&order=${encodeURIComponent("measurement_id.asc,curve_kind.asc,wavelength_nm.asc")}`;
  const key = cacheKey(url);
  const rows = await cachedRequest<ColorCurvePoint[]>(key, () => fetchJson<ColorCurvePoint[]>(url));
  const map: Record<string, Partial<Record<ColorCurveKind, ColorCurvePoint[]>>> = {};
  rows.forEach((row) => {
    const measurementId = row.measurement_id;
    if (!measurementId) return;
    if (!map[measurementId]) {
      map[measurementId] = {};
    }
    const list = map[measurementId][row.curve_kind] || [];
    list.push(row);
    map[measurementId][row.curve_kind] = list;
  });
  return map;
}

export async function fetchColorAbsorbancePerMmAtWavelength(
  ids: string[],
  wavelengthNm = 400
): Promise<Record<string, number | null>> {
  if (!ids.length) return {};
  const baseUrl = ensureBaseUrl();
  const filter = buildUuidFilter(ids);
  const numericWavelength = Number(wavelengthNm);
  const wavelengthFilter = Number.isFinite(numericWavelength) ? `eq.${numericWavelength}` : "eq.400";
  const url = `${baseUrl}/color_measurement_curve_points?select=${encodeURIComponent(
    "measurement_id,value"
  )}&measurement_id=${encodeURIComponent(filter)}&curve_kind=eq.absorbance_per_mm&wavelength_nm=${encodeURIComponent(wavelengthFilter)}`;
  const key = cacheKey(url);
  const rows = await cachedRequest<Array<{ measurement_id?: string | null; value?: number | null }>>(
    key,
    () => fetchJson<Array<{ measurement_id?: string | null; value?: number | null }>>(url)
  );
  const result: Record<string, number | null> = {};
  ids.forEach((id) => {
    result[id] = null;
  });
  rows.forEach((row) => {
    const measurementId = row.measurement_id || "";
    if (!measurementId || !(measurementId in result)) return;
    const numeric = Number(row.value);
    result[measurementId] = Number.isFinite(numeric) ? numeric : null;
  });
  return result;
}

export async function fetchColorMeasurementDetail(id: string): Promise<ColorMeasurementDetail | null> {
  const rows = await fetchColorMeasurementsByIds([id]);
  if (!rows.length) return null;
  const curveMap = await fetchColorMeasurementCurves([id]);
  return {
    ...rows[0],
    curves: curveMap[id] || {}
  };
}

export async function createColorMeasurement(
  payload: ColorMeasurementPayload
): Promise<{ id: string }> {
  const row = await createRow<{ id: string }>(
    "color_measurements",
    sanitizeColorMeasurementWritePayload(payload),
    "id"
  );
  return row;
}

export async function updateColorMeasurement(
  id: string,
  payload: Partial<ColorMeasurementPayload>
): Promise<{ id: string }> {
  const row = await updateRow<{ id: string }>(
    "color_measurements",
    id,
    sanitizeColorMeasurementWritePayload(payload),
    "id"
  );
  return row;
}

export async function deleteColorMeasurement(id: string) {
  await deleteRow("color_measurements", id);
}

export async function deleteColorMeasurements(ids: string[]) {
  if (!ids.length) return;
  const baseUrl = ensureBaseUrl();
  const chunkSize = 250;
  for (let index = 0; index < ids.length; index += chunkSize) {
    const chunk = ids.slice(index, index + chunkSize);
    const filter = buildUuidFilter(chunk);
    const url = `${baseUrl}/color_measurements?id=${encodeURIComponent(filter)}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Prefer: "return=minimal"
      }
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Color measurement delete failed: ${message}`);
    }
  }
}

export async function replaceColorMeasurementCurves(
  measurementId: string,
  curves: Partial<Record<ColorCurveKind, ColorCurvePointPayload[]>>
) {
  const baseUrl = ensureBaseUrl();
  const deleteUrl = `${baseUrl}/color_measurement_curve_points?measurement_id=eq.${measurementId}`;
  const deleteResponse = await fetch(deleteUrl, { method: "DELETE" });
  if (!deleteResponse.ok) {
    const message = await deleteResponse.text();
    throw new Error(`Color curve delete failed: ${message}`);
  }

  const flattened = Object.entries(curves).flatMap(([curveKind, rows]) =>
    (rows || []).map((row) => ({
      measurement_id: measurementId,
      curve_kind: curveKind,
      wavelength_nm: Number(row.wavelength_nm),
      value: Number(row.value)
    }))
  );
  if (!flattened.length) return;

  const chunkSize = 2000;
  const insertUrl = `${baseUrl}/color_measurement_curve_points`;
  for (let index = 0; index < flattened.length; index += chunkSize) {
    const chunk = flattened.slice(index, index + chunkSize);
    await mutateRows(insertUrl, "POST", chunk, "minimal");
  }
}

function computeStats(points: ThermoStatPoint[]): ThermoStats {
  if (!points.length) {
    return {
      minTemperature: 0,
      meanTemperature: 0,
      maxTemperature: 0,
      minViscosity: 0,
      meanViscosity: 0,
      maxViscosity: 0
    };
  }
  let minT = Number.POSITIVE_INFINITY;
  let maxT = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  let sumT = 0;
  let sumV = 0;
  points.forEach((p) => {
    minT = Math.min(minT, p.temperature);
    maxT = Math.max(maxT, p.temperature);
    minV = Math.min(minV, p.viscosity);
    maxV = Math.max(maxV, p.viscosity);
    sumT += p.temperature;
    sumV += p.viscosity;
  });
  const count = points.length;
  return {
    minTemperature: minT,
    meanTemperature: sumT / count,
    maxTemperature: maxT,
    minViscosity: minV,
    meanViscosity: sumV / count,
    maxViscosity: maxV
  };
}
